// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { atom, useAtomValue, useSetAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect, useState } from "react";

import { LocalStorageKey } from ".";
import { getLocale } from "../../helpers/locale";

type LanguagePreference = "default" | "ko" | "en" | "ja" | "th" | "vi";

export const DEFAULT_LANGUAGE_PREFERENCE: LanguagePreference = "default";

export const systemLanguageAtom = atomWithStorage<string>(
  LocalStorageKey.Language,
  DEFAULT_LANGUAGE_PREFERENCE,
  {
    getItem(key: string, initialValue: string): string {
      return localStorage.getItem(key) ?? initialValue;
    },
    setItem(key: string, newValue: string) {
      localStorage.setItem(key, newValue);
    },
    removeItem: localStorage.removeItem,
  },
  { getOnInit: true }
);

type Preference = {
  language: string;
};

export const usePreference = (): Preference => {
  let language = useAtomValue(systemLanguageAtom);
  if (language === DEFAULT_LANGUAGE_PREFERENCE) {
    language = getLocale();
  }
  return {
    language: language,
  };
};

// Color scheme
export type ColorScheme = "light" | "dark" | "system";

export const colorSchemeAtom = atomWithStorage<ColorScheme>(
  LocalStorageKey.ColorScheme,
  "system",
  {
    getItem(key: string, initialValue: ColorScheme): ColorScheme {
      return (localStorage.getItem(key) as ColorScheme) ?? initialValue;
    },
    setItem(key: string, newValue: ColorScheme) {
      localStorage.setItem(key, newValue);
    },
    removeItem: localStorage.removeItem,
  },
  { getOnInit: true }
);

const getSystemColorScheme = (): "light" | "dark" => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export const resolvedColorSchemeAtom = atom((get): "light" | "dark" => {
  const colorScheme = get(colorSchemeAtom);
  if (colorScheme === "system") {
    return getSystemColorScheme();
  }
  return colorScheme;
});

export const useColorScheme = () => {
  const colorScheme = useAtomValue(colorSchemeAtom);
  const setColorScheme = useSetAtom(colorSchemeAtom);
  const resolvedFromAtom = useAtomValue(resolvedColorSchemeAtom);

  // Track OS preference changes in real-time when "system" is selected
  const [systemPreference, setSystemPreference] = useState<"light" | "dark">(
    getSystemColorScheme
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? "dark" : "light");
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const resolvedColorScheme =
    colorScheme === "system" ? systemPreference : resolvedFromAtom;

  // Toggle dark-theme class on document.body for global CSS targeting
  useEffect(() => {
    if (resolvedColorScheme === "dark") {
      document.body.classList.add("dark-theme");
    } else {
      document.body.classList.remove("dark-theme");
    }
  }, [resolvedColorScheme]);

  return { colorScheme, resolvedColorScheme, setColorScheme };
};

// Keyed by `AutoUpdateFetchKeyButton` `settingId`.
type AutoRefreshIntervals = Record<string, number | null>;

/** Shortest interval accepted from storage; the dropdown's own floor is 5s. */
const MIN_INTERVAL_MS = 1_000;

// A stored `null` is an explicit "Off" and must survive. Everything else is
// untrusted input that reaches `setInterval` and `dayjs.duration()` — a `0` or
// a `1` there is a refetch storm against the Manager.
const parseIntervals = (stored: string | null): AutoRefreshIntervals | null => {
  if (!stored) return null;
  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null;
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([, value]) =>
          value === null ||
          (typeof value === "number" && value >= MIN_INTERVAL_MS)
      )
    ) as AutoRefreshIntervals;
  } catch {
    return null;
  }
};

export const autoRefreshIntervalsAtom = atomWithStorage<AutoRefreshIntervals>(
  LocalStorageKey.AutoRefreshIntervals,
  {},
  {
    getItem: (key: string, initialValue: AutoRefreshIntervals) =>
      parseIntervals(localStorage.getItem(key)) ?? initialValue,
    setItem: (key: string, newValue: AutoRefreshIntervals) => {
      localStorage.setItem(key, JSON.stringify(newValue));
    },
    removeItem: (key: string) => {
      localStorage.removeItem(key);
    },
    // Without this, each tab keeps a stale copy of the whole record and the
    // next write there drops whatever another tab chose.
    subscribe: (
      key: string,
      callback: (value: AutoRefreshIntervals) => void,
      initialValue: AutoRefreshIntervals
    ) => {
      const onStorage = (event: StorageEvent) => {
        if (event.key !== key) return;
        callback(parseIntervals(event.newValue) ?? initialValue);
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    },
  },
  { getOnInit: true }
);
